import LoadingUI from 'extensions/loading-ui';
import ParameterValidator from '@mvps-genesis/parameter-validator';
import {MessageContainer, Message} from 'extensions/messages';

/*
 *--------------------------------------------------------------------------
 * AjaxWishlistBase
 *--------------------------------------------------------------------------
 *
 * Base class for asynchronous functionality classes that handle Miva
 * wishlist actions.
 *
 */
export default class AjaxWishlistActionBase {

	/**
	 * Parameter Validator class object
	 *
	 * @var	{ParameterValidator} @mvps-genesis/parameter-validator
	 */
	#validator;

	/**
	 * Object containing the action input name and value
	 *
	 * @var {Object}
	 */
	actionInput;

	/**
	 * Loading UI functionality object
	 *
	 * @var {LoadingUI}
	 */
	loadingUI;

	/**
	 * Used for response message handling
	 *
	 * @var {MessageContainer} extensions/messages
	 */
	messageContainer;

	/**
	 * The message configuration options
	 *
	 * @var {Object}
	 */
	messageOptions;

	/**
	 * Create a new AjaxWishlistActionBase object.
	 *
	 * @param  {Object}              options.actionInput            Object containing the action input name and value.
	 * @param  {HTMLElement|String}  options.loadingElement         The loading UI element.
	 * @param  {HTMLElement|String}  options.loadingOverlayElement  The loading UI overlay element.
	 * @param  {HTMLElement|String}  options.messageElement         The message container element.
	 * @param  {Object}              options.messageOptions         The message configuration options object.
	 */
	constructor ({
		actionInput,
		loadingElement,
		loadingOverlayElement,
		messageElement,
		messageOptions
	} = {}) {
		this.#validator = new ParameterValidator(this);

		this.actionInput = this.#validator.isObject('actionInput', actionInput);
		this.#validator.isString('actionInput.name', this.actionInput.name);
		this.#validator.isString('actionInput.value', this.actionInput.value);

		this.messageContainer = new MessageContainer({
			container: this.#validator.isElement('messageElement', messageElement)
		});

		this.messageOptions = this.#validator.isObject('messageOptions', messageOptions);
		this.#validator.isObject('messageOptions.error', messageOptions.error);
		this.#validator.isObject('messageOptions.success', messageOptions.success);

		this.setLoadingUI(loadingElement, loadingOverlayElement);
	}

	/**
	 * Set the loading UI property
	 *
	 * @param {HTMLElement|String} loadingElement        The loading element.
	 * @param {HTMLElement|String} loadingOverlayElement The loading overlay element.
	 */
	setLoadingUI (loadingElement, loadingOverlayElement) {
		const element = this.#validator.isElement('setLoadingUI.loadingElement', loadingElement, {required: false});

		if (!element) {
			return;
		}

		const overlayElement = this.#validator.isElement('setLoadingUI.overlayElement', loadingOverlayElement, {required: false});

		this.loadingUI = new LoadingUI(element, {
			overlayElement: overlayElement ?? false
		});
	}

	/**
	 * Add a new message to the message container
	 *
	 * @param {String} message        The message.
	 * @param {Object} messageOptions The message configuration options.
	 */
	createMessage (message, messageOptions) {
		this.messageContainer.add(new Message(message, messageOptions));
	}

	/**
	 * Retrieve the validator class object.
	 *
	 * @return {ParameterValidator} @mvps-genesis/parameter-validator
	 */
	getValidator () {
		return this.#validator;
	}
}
